import { useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  APP_SETUP_PAGE,
  FileSettings,
  ModuleSettingsItem,
} from 'src/constants';
import history from 'src/history';
import { AppSettingsModalContext } from 'src/legacy/components/Modals/AppSettingsModalContext';
import { SetupModulePageDescription } from 'src/legacy/components/Settings/AppSetupPage/SetupApp';
import SetupAppBreadCrumbs from 'src/legacy/components/Settings/AppSetupPage/SetupApp/SetupAppBreadCrumbs';
import { useEditingApp } from 'src/legacy/components/Settings/AppSetupPage/SetupApp/useEditingApp';
import { useAppSetupPaths } from 'src/legacy/components/Settings/AppSetupPage/useAppSetupPaths';
import { ModuleContainer } from 'src/legacy/components/Settings/ModuleContainer';
import { SettingsPageForm } from 'src/legacy/components/Settings/SettingsPageForm';
import { useGetInstallsQuery } from 'src/services/api/applicationsApi';
import { RootState } from 'src/store';
import { updateFileSettings } from 'src/store/settings/actions';

import { useFileSettingsForm } from './useFileSettingsForm';

const FileSettingsPage = () => {
  const { folderId, addAppPagePath } = useAppSetupPaths();
  const dispatch = useDispatch();
  const modalCtx = React.useContext(AppSettingsModalContext);
  const filesModule = useEditingApp();
  const fileSettings = useSelector(
    (state: RootState) => state.settings.fileSettings,
  );
  const { data: { moduleSettings } = {} } = useGetInstallsQuery();

  const initialValues = React.useMemo(
    () => ({
      id: filesModule?.id || '',
      // Module can be hardcoded for core apps.
      type: 'module' as const,
      disabled: Boolean(filesModule?.disabled),
      label: filesModule?.label || '',
      icon: filesModule?.icon || '',
      clientFileDeleteDisabled: Boolean(fileSettings?.clientFileDeleteDisabled),
      clientFileAddDisabled: Boolean(fileSettings?.clientFileAddDisabled),
      clientFileUpdateDisabled: Boolean(fileSettings?.clientFileUpdateDisabled),
      clientFolderUpdateDisabled: Boolean(
        fileSettings?.clientFolderUpdateDisabled,
      ),
      iuSidebarHidden:
        moduleSettings?.find((m) => m.id === filesModule?.id)
          ?.iuSidebarHidden ?? false,
    }),
    [fileSettings, filesModule, moduleSettings],
  );

  /**
   * Save file settings
   */
  const onSave = async (values: FileSettings & ModuleSettingsItem) => {
    await dispatch(
      updateFileSettings({
        fileSettings: values,
        currentFileModule: filesModule,
        folderId,
      }),
    );

    if (!modalCtx) {
      history.push(APP_SETUP_PAGE.path);
    }
  };

  const handleCancel = () => {
    if (modalCtx) {
      modalCtx.onClose();
    } else {
      history.push(addAppPagePath);
    }
  };

  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <ModuleContainer description={<SetupModulePageDescription />}>
      <SettingsPageForm
        BreadcrumbsComponent={<SetupAppBreadCrumbs />}
        useFormHook={useFileSettingsForm}
        initialFormValue={initialValues}
        handleSave={onSave}
        actionLabel={filesModule?.disabled ? 'Enable' : 'Save'}
        shouldShowPageAction={filesModule?.disabled}
        // for enabled file module we just reset the form changes on "Cancel" click
        // and if there are no changes in form we don't show the form actions
        handleCancel={filesModule?.disabled ? handleCancel : undefined}
        showBackButton={isSm}
        onBackButtonClick={modalCtx?.onClose ?? (() => history.goBack())}
      />
    </ModuleContainer>
  );
};

export default FileSettingsPage;
